'use client'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import { Typo3Image, Typo3Openings } from '@/types'
import {
  BaseLink,
  CardDate,
  Chip,
  Img,
  OpeningStatus,
  Title,
  TourBadge,
  TourInfo
} from '@/ui/components'
import { BaseLinkProps } from '@/ui/components/Base/type'
import { generateAltText } from '@/utils'
import { MEDIA } from '@spot'
import { cva } from 'class-variance-authority'
import { memo } from 'react'

// TODO: variant restBarShop check opening hours
import type { TourBadgeProps } from '../TourBadge/TourBadge'
import type { TourInfoProps } from '../TourInfo/TourInfo'

interface CardMainProps extends React.HTMLAttributes<HTMLAnchorElement> {
  title?: string
  subTitle?: string
  image?: Typo3Image
  description?: string
  link?: BaseLinkProps
  cardDate?: Date | string | undefined
  date?: string
  location?: string
  distance?: string
  isTopEvent?: boolean
  openings?: Typo3Openings
  tourInfo?: TourInfoProps & TourBadgeProps
  todayOpen?: 'closed' | 'open' | 'onRequest'
  overline?: string
  priceText?: string
  priceTextInfo?: string
  variant?: 'content' | 'event' | 'hotel' | 'restBarShop' | 'tour' | 'topEvent'
}

const CardMain = ({
  title,
  subTitle,
  image,
  link,
  description,
  className,
  cardDate,
  variant = 'content',
  location,
  isTopEvent,
  date,
  openings,
  todayOpen,
  distance,
  tourInfo,
  overline,
  priceText,
  priceTextInfo,
  ...props
}: CardMainProps) => {
  //const D = useStoreI18n.use.dictionary()
  const currentLocale = useStoreI18n.use.currentLocale()

  const imageAlt = generateAltText('story', title, subTitle, currentLocale)

  const imageVars = cva('', {
    variants: {
      variant: {
        content: 'aspect-video',
        event: 'aspect-video',
        topEvent: 'aspect-video',
        hotel: 'aspect-video',
        restBarShop: 'aspect-video',
        tour: 'aspect-video'
      }
    },
    defaultVariants: {
      variant: 'content'
    }
  })

  const contentVars = cva('py-sm', {
    variants: {
      variant: {
        content: 'gap-y-sm text-neutral-600',
        event: 'gap-y-sm text-neutral-600', //
        topEvent: 'gap-y-sm', //
        hotel: 'gap-y-sm',
        restBarShop: 'gap-y-2xs text-neutral-600',
        tour: 'gap-y-sm text-neutral-600'
      }
    },
    defaultVariants: {
      variant: 'content'
    }
  })

  return (
    <BaseLink
      nextLink={true}
      className={'c-card-main block w-full'}
      fallback={'div'}
      {...link}
      {...props}
    >
      <div
        className={cn(
          'group text-primary-100 relative md:flex md:flex-col',
          className
        )}
      >
        <div className={cn('relative bg-neutral-200', imageVars({ variant }))}>
          {isTopEvent === true && (
            <div className="pl-xs pb-xs bg-accent-300 absolute top-0 right-0 z-10">
              <Chip
                variant="topEvent"
                size="s"
                iconLeft="material-symbols:star"
              >
                Top Event
              </Chip>
            </div>
          )}
          {image && (
            <Img
              data={image}
              alt={imageAlt}
              objectCover
              crop={{
                width: 442,
                height: 248
              }}
              fill
              className={'h-full w-full'}
              hoverGroupEffect
              sizes={`${MEDIA.tablet} 45rem`}
              quality={90}
            />
          )}

          <CardDate
            mode={variant == 'event' ? 'inverted' : 'default'}
            className="absolute bottom-0 left-0 z-10"
          >
            {cardDate
              ? new Date(cardDate).toLocaleDateString(currentLocale, {
                  day: 'numeric',
                  month: 'short'
                })
              : undefined}
          </CardDate>
          {(openings || todayOpen) && (
            <OpeningStatus itemName={todayOpen} data={openings} />
          )}
          <TourBadge
            difficulty={tourInfo?.difficulty}
            isStatusClosed={tourInfo?.isStatusClosed}
            isStatusOpen={tourInfo?.isStatusOpen}
            difficultyLabel={tourInfo?.difficultyLabel}
          />
        </div>

        <div className={cn('flex flex-col', contentVars({ variant }))}>
          {overline && <div className="text-neutral-400">{overline}</div>}
          <Title as="h4" className={'h4 line-clamp-3'}>
            {title}
          </Title>
          <div className="textstyle-md-regular line-clamp-3">
            {subTitle && <p className="text-neutral-400">{subTitle}</p>}
            {location && <p>{location}</p>}
            {description && <p>{description}</p>}
            {date && <p>{date}</p>}
            {distance && <p className="pt-2xs">{distance}</p>}
          </div>
          <TourInfo
            distance={tourInfo?.distance}
            duration={tourInfo?.duration}
            altitudeUphill={tourInfo?.altitudeUphill}
            altitudeDownhill={tourInfo?.altitudeDownhill}
          />
        </div>
      </div>
    </BaseLink>
  )
}

CardMain.displayName = 'CardMain'

export default memo(CardMain)
